import { useEffect, useMemo, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const DEFAULT_CENTER: [number, number] = [44.5, 11.3];
const DEFAULT_ZOOM = 6;

export type MapPoint = {
  id: string;
  title: string;
  address: string | null;
  lat: number;
  lon: number;
};

export type LodgingMapPoint = {
  id: string;
  title: string;
  lat: number;
  lon: number;
};

function makeIcon(n: number): L.DivIcon {
  return L.divIcon({
    className: 'cd-marker',
    html: `<div class="cd-marker-pin"><span>${n}</span></div>`,
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    popupAnchor: [0, -36],
  });
}

const LODGING_ICON: L.DivIcon = L.divIcon({
  className: 'cd-marker cd-marker-lodging',
  html: `<div class="cd-marker-pin cd-marker-pin-lodging"><span>🛏️</span></div>`,
  iconSize: [30, 38],
  iconAnchor: [15, 38],
  popupAnchor: [0, -34],
});

const PICK_ICON: L.DivIcon = L.divIcon({
  className: 'cd-marker cd-marker-pick',
  html: `<div class="cd-marker-pin"><span>＋</span></div>`,
  iconSize: [32, 40],
  iconAnchor: [16, 40],
});

function FitBounds({
  points,
  lodgingPoints,
  pick,
}: {
  points: MapPoint[];
  lodgingPoints: LodgingMapPoint[];
  pick: { lat: number; lon: number } | null;
}) {
  const map = useMap();
  useEffect(() => {
    const all: [number, number][] = [
      ...points.map((p) => [p.lat, p.lon] as [number, number]),
      ...lodgingPoints.map((p) => [p.lat, p.lon] as [number, number]),
      ...(pick ? [[pick.lat, pick.lon] as [number, number]] : []),
    ];
    if (all.length === 0) return;
    if (all.length === 1) {
      map.setView(all[0]!, Math.max(map.getZoom(), 11));
      return;
    }
    map.fitBounds(L.latLngBounds(all), { padding: [40, 40], maxZoom: 14 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    points.map((p) => `${p.id}:${p.lat}:${p.lon}`).join('|'),
    lodgingPoints.map((p) => `${p.id}:${p.lat}:${p.lon}`).join('|'),
    pick?.lat,
    pick?.lon,
  ]);
  return null;
}

function ClickPicker({ onPick }: { onPick: (lat: number, lon: number) => void }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

/**
 * Abilita/disabilita le interazioni della mappa che "rubano" il touch
 * sui dispositivi mobile (drag, pinch-zoom, double-tap-zoom). Il tap singolo
 * resta sempre attivo, così l'utente può ancora piazzare coordinate sulla
 * mappa anche in modalità passiva. Su desktop active=true sempre.
 */
function MapInteractionController({ active }: { active: boolean }) {
  const map = useMap();
  useEffect(() => {
    const handlers = [map.dragging, map.touchZoom, map.doubleClickZoom];
    if (active) handlers.forEach((h) => h?.enable());
    else handlers.forEach((h) => h?.disable());
  }, [map, active]);
  return null;
}

export function PlanMap({
  points,
  lodgingPoints = [],
  pick = null,
  onPick,
  routePolyline = null,
  height = 380,
}: {
  points: MapPoint[];
  /** Alloggi usati come punto di routing della tappa: mostrati come marker letto. */
  lodgingPoints?: LodgingMapPoint[];
  pick?: { lat: number; lon: number } | null;
  onPick?: (lat: number, lon: number) => void;
  /** GeoJSON LineString [lon, lat][] dal routing OSRM. Quando passata, sostituisce la linea retta. */
  routePolyline?: [number, number][] | null;
  height?: number | string;
}) {
  const initialCenter = useMemo<[number, number]>(() => {
    if (points[0]) return [points[0].lat, points[0].lon];
    if (pick) return [pick.lat, pick.lon];
    return DEFAULT_CENTER;
  }, [points, pick]);

  const polyline = useMemo<[number, number][]>(
    () => points.map((p) => [p.lat, p.lon]),
    [points],
  );

  // Su touch (pointer coarse) la mappa parte passiva: lo swipe verticale
  // scorre la pagina invece di pannare la mappa. Il tap singolo continua a
  // funzionare (per piazzare coordinate). Un pulsante in alto a destra attiva
  // le interazioni quando l'utente vuole davvero esplorare la mappa.
  const [isTouch] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(pointer: coarse)').matches,
  );
  const [active, setActive] = useState(!isTouch);

  return (
    <div
      className={`map-wrap${onPick ? ' picking' : ''}${isTouch && !active ? ' passive' : ''}`}
      style={{ height }}
    >
      <MapContainer
        center={initialCenter}
        zoom={DEFAULT_ZOOM}
        scrollWheelZoom
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          maxZoom={19}
        />
        <FitBounds points={points} lodgingPoints={lodgingPoints} pick={pick} />
        <MapInteractionController active={active} />
        {onPick && <ClickPicker onPick={onPick} />}

        {/* Quando abbiamo la rotta stradale OSRM la disegniamo solida; altrimenti
            ripieghiamo sulla linea retta tratteggiata come placeholder. */}
        {routePolyline && routePolyline.length >= 2 ? (
          <Polyline
            positions={routePolyline.map(([lon, lat]) => [lat, lon] as [number, number])}
            // dashArray: null va passato esplicitamente perchè Leaflet.setStyle
            // non azzera le proprieta omesse: se prima era renderizzata la
            // fallback tratteggiata, senza questo la dashArray resterebbe
            // applicata sopra la polilinea OSRM continua.
            pathOptions={{ color: '#1565c0', weight: 5, opacity: 0.9, dashArray: '' }}
          />
        ) : polyline.length >= 2 ? (
          <Polyline
            positions={polyline}
            pathOptions={{ color: '#1a3a52', weight: 3, opacity: 0.45, dashArray: '6 6' }}
          />
        ) : null}

        {points.map((p, i) => (
          <Marker key={p.id} position={[p.lat, p.lon]} icon={makeIcon(i + 1)}>
            <Popup>
              <div className="popup">
                <strong>
                  {i + 1}. {p.title}
                </strong>
                {p.address && <div className="muted">{p.address}</div>}
                <div className="coords">
                  {p.lat.toFixed(5)}, {p.lon.toFixed(5)}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}

        {lodgingPoints.map((p) => (
          <Marker key={`lodging-${p.id}`} position={[p.lat, p.lon]} icon={LODGING_ICON}>
            <Popup>
              <div className="popup">
                <strong>🛏️ {p.title}</strong>
                <div className="muted">Alloggio (usato per il calcolo dei km)</div>
                <div className="coords">
                  {p.lat.toFixed(5)}, {p.lon.toFixed(5)}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}

        {pick && (
          <Marker position={[pick.lat, pick.lon]} icon={PICK_ICON}>
            <Popup>
              <div className="popup">
                <strong>Posizione scelta</strong>
                <div className="coords">
                  {pick.lat.toFixed(5)}, {pick.lon.toFixed(5)}
                </div>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>
      {onPick && (
        <div className="map-hint">📍 Clicca sulla mappa per scegliere la posizione</div>
      )}
      {isTouch && (
        <button
          type="button"
          className="map-activate-btn"
          onClick={() => setActive((v) => !v)}
          aria-pressed={active}
          title={
            active
              ? 'Tap per bloccare la mappa (lo swipe scorrera la pagina)'
              : 'Tap per attivare drag/zoom della mappa'
          }
        >
          {active ? '🔓 Mappa attiva' : '🖐 Attiva mappa'}
        </button>
      )}
    </div>
  );
}
